import { cubeKey, cubeNeighbor, cubeRange } from "./cube.js";
import { hexToPixel, pointAverage } from "./layout.js";
/** Unit layout used when only topology (not pixels) is needed. */
export const UNIT_LAYOUT = {
    orientation: "flat",
    size: 1,
    origin: { x: 0, y: 0 },
};
/**
 * Sort the three hexes of a corner into a stable canonical order by their key,
 * so a corner found from any of its three hexes yields the same `hexes` tuple.
 */
export function sortHexes(a, b, c) {
    const sorted = [a, b, c].sort((x, y) => (cubeKey(x) < cubeKey(y) ? -1 : 1));
    return [sorted[0], sorted[1], sorted[2]];
}
/** A corner's coordinate identity: the sum of its three hex coords. */
function cornerCoord(t) {
    return {
        q: t[0].q + t[1].q + t[2].q,
        r: t[0].r + t[1].r + t[2].r,
        s: t[0].s + t[1].s + t[2].s,
    };
}
/**
 * Generate the full board graph for a hexagon of the given radius.
 *
 * Topology is fully determined by `radius` (independent of `layout`, which only
 * sets pixel positions), so the server and client each call this and obtain an
 * identical graph — only mutable node state is synced.
 *
 * Node **ids are assigned from a canonical sort of each corner's coordinate**,
 * NOT from the order corners are discovered. So a coordinate always maps to the
 * same id — for a given `radius` it is stable between matches and survives any
 * future change to the discovery loop below — while staying dense (`0..N-1`) so
 * `nodes` / `adjacency` index directly and ids stay one `uint16` on the wire.
 */
export function buildBoard(radius, layout = UNIT_LAYOUT) {
    const hexes = cubeRange(radius);
    const byCoord = new Map();
    const cornerKeyAt = (a, b, c) => {
        const triple = sortHexes(a, b, c);
        const coord = cornerCoord(triple);
        const key = cubeKey(coord);
        if (!byCoord.has(key)) {
            byCoord.set(key, {
                hexes: triple,
                coord,
                pixel: pointAverage(triple.map((h) => hexToPixel(layout, h))),
            });
        }
        return key;
    };
    // Undirected edges collected as unordered pairs of corner coord-keys (the ids
    // they resolve to aren't known until phase 2).
    const edgeSeen = new Set();
    const edgePairs = [];
    for (const h of hexes) {
        // Corner i is shared by this hex and its neighbours in directions i, i+1.
        const ck = [];
        for (let i = 0; i < 6; i++) {
            ck.push(cornerKeyAt(h, cubeNeighbor(h, i), cubeNeighbor(h, i + 1)));
        }
        // Each hex's 6 perimeter edges connect consecutive corners; their union is
        // exactly the honeycomb movement graph.
        for (let i = 0; i < 6; i++) {
            const a = ck[i];
            const b = ck[(i + 1) % 6];
            if (a === b)
                continue;
            const key = a < b ? `${a}#${b}` : `${b}#${a}`;
            if (edgeSeen.has(key))
                continue;
            edgeSeen.add(key);
            edgePairs.push([a, b]);
        }
    }
    // Phase 2 — assign ids by a canonical order of the corner COORDINATE: sort by
    // (q, r) (s is derived, so (q, r) is a total order over coordinates). The id
    // is therefore a deterministic function of geometry.
    const sorted = [...byCoord.entries()].sort(([, a], [, b]) => a.coord.q - b.coord.q || a.coord.r - b.coord.r);
    const idByCoord = new Map();
    const nodes = sorted.map(([key, raw], id) => {
        idByCoord.set(key, id);
        return { id, coord: raw.coord, hexes: raw.hexes, pixel: raw.pixel };
    });
    // Phase 3 — materialise edges + adjacency against the canonical ids.
    const edges = [];
    const adjacency = nodes.map(() => []);
    for (const [ka, kb] of edgePairs) {
        const a = idByCoord.get(ka);
        const b = idByCoord.get(kb);
        const lo = Math.min(a, b);
        const hi = Math.max(a, b);
        edges.push([lo, hi]);
        adjacency[a].push(b);
        adjacency[b].push(a);
    }
    edges.sort((e, f) => e[0] - f[0] || e[1] - f[1]);
    for (const list of adjacency)
        list.sort((x, y) => x - y);
    return { radius, hexes, nodes, edges, adjacency, idByCoord };
}
//# sourceMappingURL=board.js.map